import React, { useMemo, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/Edit';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SearchIcon from '@material-ui/icons/Search';
import typ from 'design-v2/globals/typography.module.css';
import PopoverContainer from './PopoverContainer';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import Radio from '@material-ui/core/Radio';
import Box from '@material-ui/core/Box';
import Pagination from '@material-ui/lab/Pagination';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableRow from '@material-ui/core/TableRow';
import { IUsersTable } from './types';
import { ITagIcon } from 'external-components/tag-icon/types';
import TagIcon from 'external-components/tag-icon';
import { ICustomer } from 'shared-types/index';
import style from './style.module.css';
import CustomerSearchResultTab from 'design-v2/common/CustomerSearchResultTab/CustomerSearchResultTab';

interface IColumn {
  id: keyof ICustomer;
  label: string;
  width: string;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  render?: (arg: any, customer: ICustomer) => React.ReactNode;
  sortAble?: boolean;
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    borderRadius: '4px',
  },
});

type Order = 'asc' | 'desc';
const PER_PAGE_WEB = 7;
const PER_PAGE_MOBILE = 5;

export default function UsersTable(props: IUsersTable) {
  const {
    customers,
    handleViewCustomer,
    handleEditCustomer,
    searchResultStatus,
    isMobile,
    setView,
  } = props;
  const [selectedId, setSelectedId] = useState<string>(null);
  const classes = useStyles();
  const [page, setPage] = useState<number>(1);
  const PER_PAGE = isMobile ? PER_PAGE_MOBILE : PER_PAGE_WEB;

  const count = Math.ceil(customers.length / PER_PAGE);
  const shownCustomers = usePagination(customers, PER_PAGE);

  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof ICustomer>(null);

  const handleChange = (_: any, pape: number) => {
    setPage(pape);
    shownCustomers.jump(pape);
  };
  const _handleSelectedCustomer = (customer: ICustomer) => {
    setSelectedId(customer._id);
    handleViewCustomer(customer);
  };
  const idColumn = {
    id: 'idColumn',
    label: '',
    width: '40px',
    align: 'left',
    sortAble: false,
    render: (_: any, customer: ICustomer): React.ReactNode => (
      <Radio
        size="small"
        checked={selectedId === customer._id}
        onChange={(event) => {
          if (event.target.checked) {
            setSelectedId(customer._id);
            handleViewCustomer(customer);
          }
        }}
      />
    ),
  };
  const idColumnMobileEditIcon = {
    id: 'editIconColumn',
    label: '',
    width: '40px',
    align: 'left',
    sortAble: false,
    render: (_: any, customer: ICustomer): React.ReactNode => (
      <IconButton
        color="primary"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          handleEditCustomer(customer);
        }}
        aria-label="edit-customer"
      >
        <EditIcon />
      </IconButton>
    ),
  };
  const radioWithFirstNameColumn = {
    id: 'firstName',
    label: 'First name',
    width: '25%',
    sortAble: true,
    render: (value: any, customer: ICustomer): React.ReactNode => {
      return (
        <Tooltip title={value} placeholder="top">
          <div
            className={classNames({
              [style.radioMobile]: true,
            })}
          >
            <Radio
              size="small"
              checked={selectedId === customer._id}
              onChange={(event) => {
                if (event.target.checked) {
                  setSelectedId(customer._id);
                  handleViewCustomer(customer);
                }
              }}
            />
            <span className={style.ellipsis}>{value}</span>
          </div>
        </Tooltip>
      );
    },
  };
  const firstNameColumn = {
    id: 'firstName',
    label: 'First name',
    width: '20%',
    sortAble: true,
  };

  const columns: IColumn[] = useMemo(() => {
    return [
      isMobile ? idColumnMobileEditIcon : idColumn,
      isMobile ? radioWithFirstNameColumn : firstNameColumn,
      {
        id: 'lastName',
        label: 'Surname',
        width: '20%',
        sortAble: true,
      },
      {
        id: 'phone',
        label: 'Phone',
        width: '15%',
        sortAble: true,
      },
      {
        id: 'email',
        label: 'Email',
        width: '15%',
        sortAble: true,
      },
      {
        id: 'tags',
        label: 'Tags',
        width: '15%',
        sortAble: false,
        render: (tags: ITagIcon[]): React.ReactNode => {
          const Tags = sortBy(tags, (item) => item.name.toLowerCase()).map((tag) => {
            return (
              <div style={{ marginRight: 4, display: 'inline-block', marginTop: 4 }} key={tag.name}>
                <TagIcon {...tag} />
              </div>
            );
          });
          return tags?.length < 6 ? <>{Tags}</> : <PopoverContainer>{Tags}</PopoverContainer>;
        },
      },
      {
        id: 'company',
        label: 'Company',
        width: '15%',
        sortAble: true,
      },
      isMobile ? null : idColumnMobileEditIcon,
    ].filter((i) => i) as IColumn[];
  }, [selectedId]);

  const handleRequestSort = (property: keyof ICustomer) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div style={{ width: '100%' }}>
      <Paper className={classes.root} style={{ width: '100%' }}>
        <TableContainer
          style={{ overflow: 'hide' }}
          className={classNames({
            //[style.tableOnDm]: isMobile,
            // [style.usersTableSticky]: isMobile && customers?.length > 0,
          })}
        >
          <Table
            className={classNames({
              [style.tableContainer]: true,
              [style.usersTable]: true,
              [style.usersTableMobile]: isMobile,
            })}
            stickyHeader={!isMobile}
            aria-label="Existing Customers"
          >
            {!isMobile && (
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      className={style.noneBorder}
                      key={column.id + '_' + 'head'}
                      align={column.align}
                      style={{ width: column.width }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                        hideSortIcon={!column.sortAble}
                        className={style.iconDirection}
                      >
                        <span
                          style={{ whiteSpace: 'nowrap' }}
                          className={classNames({
                            [style.label]: true,
                            [typ.typSize2]: true,
                            [typ.typBold]: true,
                          })}
                        >
                          {column.label}
                        </span>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {customers?.length ? (
                stableSort(shownCustomers.currentData(), getComparator(order, orderBy)).map(
                  (customer) => {
                    if (isMobile) {
                      return (
                        <CustomerSearchResultTab
                          customer={customer}
                          selectedId={selectedId}
                          handleViewCustomer={_handleSelectedCustomer}
                          handleEditCustomer={handleEditCustomer}
                          key={customer._id}
                        />
                      );
                    }
                    return (
                      <TableRow
                        className={style.clickableRow}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={customer._id}
                        onClick={() => handleViewCustomer(customer)}
                      >
                        {columns.map((column, index) => {
                          const value = customer[column.id as keyof ICustomer];
                          return (
                            <TableCell
                              className={style.noneBorder}
                              key={customer._id + index}
                              align={column.align}
                            >
                              <Tooltip title={column.render ? '' : value || ''} placement="top">
                                <div style={{ overflow: 'auto' }}>
                                  {column.render ? (
                                    column.render(value, customer)
                                  ) : (
                                    <span className={style.ellipsis}>{value as string}</span>
                                  )}
                                </div>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  }
                )
              ) : (
                <NoDataRow
                  length={columns.length}
                  searchResultStatus={searchResultStatus}
                  setView={setView}
                  isMobile={isMobile}
                />
              )}

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {customers?.length && customers?.length > PER_PAGE ? (
        <Box
          marginTop="8px"
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Pagination
            count={count}
            size="medium"
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handleChange}
            showFirstButton
            showLastButton
            className={style.paginationForCustomerSearchForm}
          />
        </Box>
      ) : null}
    </div>
  );
}

const NoDataRow = ({
  length,
  searchResultStatus,
  isMobile,
  setView,
}: {
  length: number;
  searchResultStatus: number;
  isMobile: boolean;
  setView: (view: any) => void;
}) => {
  return (
    <TableRow className={style.tableWithoutData}>
      <TableCell colSpan={length}>
        <Box display="flex" width="100%" height="100%" alignItems="center" justifyContent="center">
          {searchResultStatus === 0 && (
            <p
              className={classNames({
                [style.noData]: true,
                [typ.typSize2]: true,
                [typ.typBold]: true,
              })}
            >
              Search by Phone, First Name, Surname, Email or Company
            </p>
          )}
          {/*{searchResultStatus === 1 &&*/}
          {/*  <p className={classNames({*/}
          {/*    [style.noData]: true,*/}
          {/*    [typ.typSize2]: true,*/}
          {/*    [typ.typBold]: true,*/}
          {/*  })}>Loading...</p>*/}
          {/*}*/}
          {searchResultStatus === 2 && (
            <div>
              <div
                className={classNames({
                  [style.noResults]: true,
                  [typ.typSize3]: true,
                })}
              >
                <SearchIcon/> No matches found
              </div>
              {/*{!isMobile && (*/}
                <Button variant='contained' color='primary' onClick={setView}>
                  <PersonAddIcon color="inherit" />
                  <span className={style.isAllowedCreateNewCustomer}>
                    Create a new customer
                  </span>
                </Button>
              {/*)}*/}
            </div>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

function getComparator<Key extends keyof ICustomer>(
  order: Order,
  orderBy: Key
): (a: ICustomer, b: ICustomer) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: ICustomer[], comparator: (a: ICustomer, b: ICustomer) => number) {
  return array.sort((a, b) => {
    const order = comparator(a, b);
    return order;
  });
}

function descendingComparator(a: ICustomer, b: ICustomer, orderBy: keyof ICustomer) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function usePagination(data: ICustomer[], itemsPerPage: number) {
  const [currentPage, setCurrentPage] = useState(1);
  const maxPage = Math.ceil(data.length / itemsPerPage);

  function currentData() {
    const begin = (currentPage - 1) * itemsPerPage;
    const end = begin + itemsPerPage;
    return data.slice(begin, end);
  }

  function next() {
    setCurrentPage((currentPage) => Math.min(currentPage + 1, maxPage));
  }

  function prev() {
    setCurrentPage((currentPage) => Math.max(currentPage - 1, 1));
  }

  function jump(page: number) {
    const pageNumber = Math.max(1, page);
    setCurrentPage((currentPage) => Math.min(pageNumber, maxPage));
  }

  return { next, prev, jump, currentData, currentPage, maxPage };
}
